import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FileResponse } from '../interfaces/FileResponse';
import { isPdfShownInline, useSheetSource } from '../search/use-sheet-source';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon, SpinnerIcon } from '../icons/icons';
import { StageEntry, usePdfPageImage, useStageEntries } from './use-stage-entries';
import styles from './stage-mode.module.css'

export interface StageModeProps {
    items: FileResponse[],
    // Index in items; collection PDFs are expanded into one screen per page
    startIndex?: number,
    // Receives the index in items of the sheet the musician stopped at
    onClose: (index: number) => void
}

const CONTROLS_HIDE_DELAY = 2500;
const SWIPE_THRESHOLD = 50;

const NEXT_KEYS = ['ArrowRight', 'ArrowDown', 'PageDown', ' '];
const PREV_KEYS = ['ArrowLeft', 'ArrowUp', 'PageUp'];

// Full screen sheet viewer for performing: tap the sides, swipe or use a page-turner pedal (arrow/page keys)
export default function StageMode({ items, startIndex = 0, onClose }: StageModeProps) {
    const entries = useStageEntries(items);
    const [index, setIndex] = useState(0);
    const [controlsVisible, setControlsVisible] = useState(true);

    const count = entries?.length ?? 0;
    const entriesRef = useRef(entries);
    entriesRef.current = entries;

    // Once the pages are known, open on the first page of the sheet the musician picked
    useEffect(() => {
        if (!entries) return;

        const start = Math.min(Math.max(startIndex, 0), items.length - 1);
        setIndex(Math.max(entries.findIndex(e => e.itemIndex === start), 0));
        // Only when the entries arrive
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries]);

    const containerRef = useRef<HTMLDivElement>(null);
    const hideTimer = useRef<ReturnType<typeof setTimeout>>();
    const touchStart = useRef<{ x: number, y: number }>();
    const indexRef = useRef(index);
    indexRef.current = index;
    const closedRef = useRef(false);

    // Both the close button and leaving browser fullscreen end up here, so only report once
    const finish = useCallback(() => {
        if (closedRef.current) return;
        closedRef.current = true;

        onClose(entriesRef.current?.[indexRef.current]?.itemIndex ?? startIndex);
    }, [onClose, startIndex]);

    const close = useCallback(() => {
        if (document.fullscreenElement)
            document.exitFullscreen?.().catch(() => { });

        finish();
    }, [finish]);

    const go = useCallback((delta: number) => {
        // Ignore page turns while the pages are still being counted
        setIndex(i => count ? Math.min(Math.max(i + delta, 0), count - 1) : i);
    }, [count]);

    const showControls = useCallback(() => {
        setControlsVisible(true);
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_DELAY);
    }, []);

    const hideControls = useCallback(() => {
        clearTimeout(hideTimer.current);
        setControlsVisible(false);
    }, []);

    // Tapping the middle of the sheet shows or hides the buttons
    const toggleControls = useCallback(() => {
        if (controlsVisible) hideControls();
        else showControls();
    }, [controlsVisible, hideControls, showControls]);

    // Only a real mouse wakes the controls: touch screens fire a fake mousemove after every tap,
    // which would undo the toggle above
    const onPointerMove = (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') showControls();
    };

    // Real fullscreen where supported (not on iPhone); the fixed overlay covers the window otherwise
    useEffect(() => {
        const container = containerRef.current;
        container?.requestFullscreen?.().catch(() => { });

        // Leaving fullscreen with the browser's own Esc/gesture also leaves stage mode
        const onFullscreenChange = () => {
            if (!document.fullscreenElement)
                finish();
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.body.style.overflow = previousOverflow;
            clearTimeout(hideTimer.current);
        };
    }, [finish]);

    // Keep the screen on while the musician is playing
    useEffect(() => {
        let lock: any;
        const nav: any = navigator;

        const acquire = async () => {
            try {
                lock = await nav.wakeLock?.request('screen');
            } catch { }
        };
        const onVisibility = () => {
            if (document.visibilityState === 'visible')
                acquire();
        };

        acquire();
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            lock?.release?.().catch(() => { });
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (NEXT_KEYS.includes(e.key)) {
                e.preventDefault();
                go(1);
            }
            else if (PREV_KEYS.includes(e.key)) {
                e.preventDefault();
                go(-1);
            }
            else if (e.key === 'Home') setIndex(0);
            else if (e.key === 'End' && count) setIndex(count - 1);
            else if (e.key === 'Escape') close();
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [go, close, count]);

    useEffect(() => showControls(), [showControls]);

    const onTouchStart = (e: React.TouchEvent) => {
        const t = e.touches[0];
        touchStart.current = { x: t.clientX, y: t.clientY };
    };

    const onTouchEnd = (e: React.TouchEvent) => {
        const start = touchStart.current;
        touchStart.current = undefined;
        if (!start) return;

        const t = e.changedTouches[0];
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;

        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy))
            go(dx < 0 ? 1 : -1);
    };

    const current = entries?.[index];
    const isFirst = index === 0;
    const isLast = index >= count - 1;
    const title = current && !current.item.address.startsWith('http') ? current.item.address : undefined;

    return <div
        ref={containerRef}
        className={`${styles.stage} ${controlsVisible ? '' : styles.idle}`}
        role='dialog'
        aria-modal='true'
        aria-label='Modo palco'
        onPointerMove={onPointerMove}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
    >
        {current
            ? <StageSheet key={entryKey(current)} entry={current} onTapCenter={toggleControls} onTapPrev={() => go(-1)} onTapNext={() => go(1)} />
            : <div className={styles.loading}><SpinnerIcon size={36} /></div>}

        {/* Warm the cache for the neighbours so turning the page is instant */}
        {entries && !isLast && <Preload entry={entries[index + 1]} />}
        {entries && !isFirst && <Preload entry={entries[index - 1]} />}

        <div className={`${styles.topBar} ${styles.controls}`}>
            {current && <span className={styles.counter}>
                <strong>{index + 1}</strong> / {count}
                {title && <span className={styles.title}>{title}</span>}
                {current.page && <span className={styles.pageInfo}>pág. {current.page}/{current.pageCount}</span>}
            </span>}
            <button type='button' className={styles.iconButton} onClick={close} aria-label='Sair do modo palco' title='Sair (Esc)'>
                <CloseIcon size={22} />
            </button>
        </div>

        <button type='button' className={`${styles.navButton} ${styles.prev} ${styles.controls}`} onClick={() => go(-1)} disabled={isFirst} aria-label='Partitura anterior'>
            <ChevronLeftIcon size={28} />
        </button>
        <button type='button' className={`${styles.navButton} ${styles.next} ${styles.controls}`} onClick={() => go(1)} disabled={isLast} aria-label='Próxima partitura'>
            <ChevronRightIcon size={28} />
        </button>

        <div className={styles.progress} aria-hidden='true'>
            <span style={{ width: count ? `${((index + 1) / count) * 100}%` : 0 }} />
        </div>
    </div>
}

const entryKey = (entry: StageEntry) => `${entry.itemIndex}-${entry.item.serverPath}-${entry.page ?? 0}`;

interface StageSheetProps {
    entry: StageEntry,
    onTapPrev: () => void,
    onTapNext: () => void,
    onTapCenter: () => void
}

function StageSheet(props: StageSheetProps) {
    return props.entry.page
        ? <PdfPageSheet {...props} />
        : <FileSheet {...props} />
}

// A single page of a collection PDF, rendered as an image by the API
function PdfPageSheet({ entry, ...taps }: StageSheetProps) {
    const src = usePdfPageImage(entry.item, entry.page);

    if (!src)
        return <div className={styles.loading}><SpinnerIcon size={36} /></div>;

    return <SheetImage src={src} alt={`${entry.item.address} - página ${entry.page}`} {...taps} />
}

function FileSheet({ entry, ...taps }: StageSheetProps) {
    const source = useSheetSource(entry.item);

    if (!source.src)
        return <div className={styles.loading}><SpinnerIcon size={36} /></div>;

    // Inline PDFs keep their own scrolling, so only the arrows and keys turn the page
    if (isPdfShownInline(source))
        return <object className={styles.pdf} data={source.src} type='application/pdf' aria-label={entry.item.address} />;

    return <SheetImage src={source.src} alt={entry.item.address} {...taps} />
}

interface SheetImageProps extends Omit<StageSheetProps, 'entry'> {
    src: string,
    alt: string
}

function SheetImage({ src, alt, onTapPrev, onTapNext, onTapCenter }: SheetImageProps) {
    return <>
        <img className={styles.image} src={src} alt={alt} draggable={false} />
        <div className={styles.tapZones} aria-hidden='true'>
            <div onClick={onTapPrev} />
            <div onClick={onTapCenter} />
            <div onClick={onTapNext} />
        </div>
    </>
}

function Preload({ entry }: { entry: StageEntry }) {
    return entry.page
        ? <PreloadPage entry={entry} />
        : <PreloadFile entry={entry} />
}

function PreloadPage({ entry }: { entry: StageEntry }) {
    usePdfPageImage(entry.item, entry.page);
    return null;
}

function PreloadFile({ entry }: { entry: StageEntry }) {
    useSheetSource(entry.item);
    return null;
}
